import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Check,
  AlertCircle,
  Loader2,
  Cloud,
  Server,
  Database,
  Maximize2
} from 'lucide-react';
import { useUpload } from '../../contexts/UploadContext';

// Helper to format time
const formatTime = (ms: number): string => {
  if (!ms || ms < 1000) return '';
  const seconds = Math.ceil(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
};

export function UploadProgressToast() {
  const navigate = useNavigate();
  const { activeUpload, isUploading, showOverlay } = useUpload();

  // Don't show if no upload or if overlay is visible
  if (!activeUpload || showOverlay) return null;

  const { progress, filesCount } = activeUpload;
  const isComplete = progress.stage === 'complete';
  const isError = progress.stage === 'error';

  const handleClick = () => {
    navigate('/uploads');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div
        onClick={handleClick}
        className={`
          flex items-center gap-4 px-5 py-4 rounded-2xl shadow-2xl cursor-pointer
          transition-all duration-300 hover:scale-105 hover:shadow-3xl
          border min-w-[320px]
          ${isComplete
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : isError
              ? 'bg-destructive/10 border-destructive/30'
              : 'bg-card border-border'
          }
        `}
      >
        {/* Icon */}
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center shrink-0
          ${isComplete
            ? 'bg-emerald-500/20'
            : isError
              ? 'bg-destructive/20'
              : 'bg-primary/20'
          }
        `}>
          {isComplete ? (
            <Check className="w-6 h-6 text-emerald-500" />
          ) : isError ? (
            <AlertCircle className="w-6 h-6 text-destructive" />
          ) : progress.stage === 'uploading' ? (
            <Upload className="w-6 h-6 text-primary animate-pulse" />
          ) : progress.stage === 'server_receiving' ? (
            <Server className="w-6 h-6 text-primary animate-pulse" />
          ) : progress.stage === 'cloudinary_upload' ? (
            <Cloud className="w-6 h-6 text-primary animate-bounce" />
          ) : progress.stage === 'finalizing' ? (
            <Database className="w-6 h-6 text-primary animate-pulse" />
          ) : (
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-sm">
              {isComplete
                ? 'Upload Complete'
                : isError
                  ? 'Upload Failed'
                : progress.stage === 'uploading'
                  ? 'Uploading...'
                : progress.stage === 'server_receiving'
                  ? 'Receiving...'
                : progress.stage === 'cloudinary_upload'
                  ? 'Processing...'
                : progress.stage === 'finalizing'
                  ? 'Finalizing...'
                : 'Preparing...'}
            </span>
            <span className={`text-sm font-bold tabular-nums ${
              isComplete
                ? 'text-emerald-500'
                : isError
                  ? 'text-destructive'
                  : 'text-primary'
            }`}>
              {progress.percentage}%
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-2 bg-muted rounded-full overflow-hidden mb-1">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isComplete
                  ? 'bg-emerald-500'
                  : isError
                    ? 'bg-destructive'
                    : 'bg-gradient-to-r from-primary to-purple-500'
              }`}
              style={{ width: `${progress.percentage}%` }}
            />
          </div>

          <div className="text-xs text-muted-foreground">
            {isComplete
              ? `${filesCount} images uploaded`
              : isError
                ? progress.message || 'Something went wrong'
                : progress.filesProcessed !== undefined && progress.filesTotal
                  ? `${progress.filesProcessed}/${progress.filesTotal} images${progress.estimatedTimeRemaining ? ` • ~${formatTime(progress.estimatedTimeRemaining)}` : ''}`
                  : `${filesCount} images${progress.estimatedTimeRemaining ? ` • ~${formatTime(progress.estimatedTimeRemaining)}` : ''}`
            }
          </div>
        </div>

        {/* Expand icon */}
        <Maximize2 className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>
    </div>
  );
}
